/**
 * Standalone signatures that unambiguously indicate a dynamically imported chunk
 * (or its CSS) failed to load. Each is emitted only for module-script loads, so a
 * substring match is safe on its own.
 */
const CHUNK_LOAD_ERROR_SIGNATURES = [
  'failed to fetch dynamically imported module',
  'expected a javascript-or-wasm module script', // Chrome MIME refusal
  'importing a module script failed', // Safari
  'error loading dynamically imported module', // Firefox dynamic-import failure
] as const;

/**
 * Firefox surfaces the transport failure of a dynamic import as a generic
 * "NetworkError when attempting to fetch resource." — but it emits that identical
 * message for *any* failed fetch, so on its own it cannot be trusted to mean a
 * chunk-load failure. It only counts as one when the message ALSO references a
 * module/import (or a script URL), which prevents a genuine render-time network
 * error from being misclassified as a stale-deploy chunk failure and shown the
 * misleading "new version available — reload" CTA.
 */
const FIREFOX_NETWORK_ERROR_SIGNATURE =
  'networkerror when attempting to fetch resource';

/**
 * "module"/"import" references that qualify a Firefox NetworkError as a chunk
 * load — but ONLY when they appear in the message prose, not incidentally inside
 * an embedded request URL. A path segment like `/api/imports/` or `/modules/`
 * would otherwise trip this substring match and misclassify an ordinary fetch
 * failure, so URLs are stripped from the message before it is tested (see
 * `EMBEDDED_URL_PATTERN` and `isChunkLoadError`).
 */
const MODULE_REFERENCE_SIGNATURES = ['module', 'import'] as const;

/**
 * Global matcher for embedded http(s) URLs. Used both to (a) parse each embedded
 * URL so the `.js`/`.mjs` script check runs against its *pathname* only — never a
 * `.js`/`.mjs` value smuggled into a query string or fragment — and (b) strip
 * URLs from the message before the prose-only `MODULE_REFERENCE_SIGNATURES`
 * check, so a word inside a URL path can't be mistaken for a module/import
 * reference. See `isChunkLoadError`.
 */
const EMBEDDED_URL_PATTERN = /https?:\/\/[^\s'")]+/g;

/**
 * Matches a `.js` or `.mjs` script URL extension at a real terminator — the end
 * of the string, a query/hash delimiter (`?`/`#`), a message delimiter
 * (whitespace, quotes, or common punctuation), or a trailing dot that is not the
 * start of another extension. A raw `.js` substring test would match `.json`
 * (its `.js` prefix); a bare "non-alphanumeric boundary" would additionally match
 * sibling assets such as `.js.map` (source maps), `.js.gz`/`.js.br` (compressed
 * bundles), and `.js-…` — none of which are the executable chunk. Restricting to
 * these terminators keeps those non-script resources on the generic fallback path
 * while still excluding `.json`, `.jsx`, and similar look-alikes.
 */
const MODULE_SCRIPT_URL_SIGNATURE = /\.m?js(?=$|[\s?#'"),;:!]|\.(?![a-z0-9]))/;

/**
 * True when `error` looks like a dynamic-import/chunk load failure (stale deploy,
 * MIME refusal), as opposed to an application render error.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  if (error.name === 'ChunkLoadError') {
    return true;
  }
  const message = error.message.toLowerCase();
  if (CHUNK_LOAD_ERROR_SIGNATURES.some(sig => message.includes(sig))) {
    return true;
  }
  // Firefox's ambiguous NetworkError only qualifies when it also names a
  // module/import or a real .js/.mjs script URL — a bare NetworkError (or one
  // referencing a .json data URL) is a generic fetch failure, not a chunk load.
  if (!message.includes(FIREFOX_NETWORK_ERROR_SIGNATURE)) {
    return false;
  }
  // A real .js/.mjs script URL is a chunk load — but the extension must be in
  // the URL's *pathname*, not its query or fragment. A non-script URL such as
  // `…/api/redirect?next=/foo.js` carries `.js` only in the query and must stay
  // on the generic fallback path, so each embedded URL is parsed and only its
  // pathname is tested (unparseable URLs are ignored).
  const embeddedUrls = message.match(EMBEDDED_URL_PATTERN);
  if (
    embeddedUrls?.some(url => {
      try {
        return MODULE_SCRIPT_URL_SIGNATURE.test(new URL(url).pathname);
      } catch {
        return false;
      }
    })
  ) {
    return true;
  }
  // Otherwise the "module"/"import" reference must appear in the message prose,
  // NOT incidentally inside a request URL (e.g. `/api/imports/data.json`), which
  // would misclassify an ordinary fetch failure. Strip embedded URLs first.
  const messageWithoutUrls = message.replace(EMBEDDED_URL_PATTERN, ' ');
  return MODULE_REFERENCE_SIGNATURES.some(ref =>
    messageWithoutUrls.includes(ref)
  );
}

/** Extract the first http(s) URL embedded in an error message, if any (the failing chunk URL). */
export function extractChunkUrl(error: unknown): string | undefined {
  if (!(error instanceof Error)) {
    return undefined;
  }
  const match = error.message.match(/https?:\/\/[^\s'")]+/);
  return match ? match[0] : undefined;
}
